import { Component, ChangeDetectionStrategy, EventEmitter, Input, Output, SimpleChanges, OnChanges, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';

/**
 * StoragePagePreviewComponent
 * 
 * Displays a single image preview page using a presigned URL from the Storage API.
 * The component receives presigned URLs directly from StorageGridPreviewComponent,
 * which fetches them from the Storage API endpoint: GET /pub/p/{uuid}/img
 * 
 * The component handles:
 * - Loading and displaying presigned image URLs
 * - Emitting load events when images are ready
 * - Error handling for failed preview loads
 */
@Component({
    selector: 'core-storage-page-preview',
    templateUrl: './page-preview.component.html',
    styleUrls: ['./page-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StoragePagePreviewComponent implements OnChanges {
    @ViewChild('img', { read: ElementRef }) img: ElementRef<HTMLImageElement>;

    /**
     * Presigned URL for the image preview
     * This URL is obtained from the Storage API and is valid for 5 minutes
     * Example: https://download.twonas.com/previews/f47ac10b-page-1.jpg?X-Amz-Signature=...
     */
    @Input() presignedUrl: string;

    /**
     * Page number for display purposes and alt text
     */
    @Input() pageNumber: number = 1;

    /**
     * File identifier for alt text
     */
    @Input() fileId: string;

    /**
     * Event emitted when image has successfully loaded
     */
    @Output() onLoad = new EventEmitter<void>();

    /**
     * Event emitted when image fails to load
     */
    @Output() onError = new EventEmitter<any>();

    /**
     * Indicates if the image has resolved/loaded successfully
     */
    public resolved: boolean = false;

    /**
     * Indicates if the image is currently loading
     */
    public isLoading: boolean = true;

    /**
     * Error message if image failed to load
     */
    public errorMessage: string;

    protected alt: string;

    constructor(private changeDetectorRef: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['presignedUrl'] && this.presignedUrl) {
            this.resolved = false;
            this.isLoading = true;
            this.errorMessage = null;
            this.loadPreview();
        }
    }

    /**
     * Loads the image preview from the presigned URL
     * Uses native img element's onload/onerror handlers to avoid duplicate network requests
     */
    private loadPreview(): void {
        this.alt = `preview-${this.fileId}-page-${this.pageNumber}`;
        // Native img element will handle the load via template [src] binding
        // Change detector will trigger when presignedUrl changes
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Handler called by img element when image loads successfully
     * Called from template: (load)="onLoadImg()"
     */
    public onLoadImg(): void {
        this.resolved = true;
        this.isLoading = false;
        this.errorMessage = null;
        this.changeDetectorRef.markForCheck();
        this.onLoad.emit();
    }

    /**
     * Handler called by img element when image fails to load
     * Called from template: (error)="onLoadImgError($event)"
     */
    public onLoadImgError(error: any): void {
        this.resolved = false;
        this.isLoading = false;
        this.errorMessage = 'Failed to load preview image';
        this.changeDetectorRef.markForCheck();
        this.onError.emit(error);
    }


}
